import { getBuyerId, getGuestId, getUserType } from 'dibs-cookie-jar';
import { type FC, useEffect, useCallback, useRef, Suspense } from 'react';
import { useQueryLoader, type PreloadedQuery } from 'react-relay';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy.ts';
import { CUSTOMER_TYPE_TRADE } from 'dibs-buyer-layout/exports/userStorageConstants.ts';
import { getUserSessionCountryCode } from 'dibs-regional-info/src/regionalInfoHelpers.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import {
    PDP_INFINITE_SCROLL_CAROUSEL_SL_COUNT,
    PDP_INFINITE_SCROLL_SL_INITIAL_INFLATE,
    PDP_INFINITE_SCROLL_SPONSORED_PAGE_SIZE,
} from '../../PdpInfiniteScrollGrid/pdpInfiniteScrollGridConstants.ts';
// components
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { PdpRespMarketplaceLoadingCarousel } from '../PdpRespMarketplaceLoadingCarousel/PdpRespMarketplaceLoadingCarousel.tsx';
import PdpSharedSellerModuleLoader from '../../shared/PdpSharedSellerModule/PdpSharedSellerModuleLoader.tsx';

// lazy components
const PdpRespMarketplaceLayoutSecondaryLazy = dibsLazy(
    'PdpRespMarketplaceLayoutSecondary',
    () =>
        import(
            /* webpackChunkName: "PdpRespMarketplaceLayoutSecondary" */ './PdpRespMarketplaceLayoutSecondary.tsx'
        )
);

// types
import {
    pdpRespMarketplaceSecondaryRootQuery,
    type pdpRespMarketplaceSecondaryRootQueryType,
    type pdpRespMarketplaceSecondaryRootQuery$variables as SecondaryRelayVariables,
} from '../../../queries/pdpRespMarketplaceSecondaryRoot.ts';

const LoadingCarousel: FC = () => {
    const { isPdpSellerModuleTestVariant } = useServerVarsContext();

    return (
        <>
            <PdpRespMarketplaceLoadingCarousel />
            {isPdpSellerModuleTestVariant && <PdpSharedSellerModuleLoader />}
            <PdpRespMarketplaceLoadingCarousel />
        </>
    );
};

export const PdpRespMarketplaceLayoutSecondaryRenderer: FC<{
    serverSecondaryQueryRef?: PreloadedQuery<pdpRespMarketplaceSecondaryRootQueryType> | null;
    relayVariables: SecondaryRelayVariables;
}> = ({ relayVariables: serverRelayVariables, serverSecondaryQueryRef }) => {
    const [secondaryQueryRef, loadSecondaryQuery] =
        useQueryLoader<pdpRespMarketplaceSecondaryRootQueryType>(
            pdpRespMarketplaceSecondaryRootQuery,
            serverSecondaryQueryRef
        );
    const relayVariables = useRef(serverRelayVariables);
    const layoutVisibilityRef = useRef<HTMLDivElement>(null);
    // Prevents repeated loadSecondaryQuery calls (each disposes the previous PreloadedQuery).
    const hasLoadedRef = useRef(!!serverSecondaryQueryRef);
    const { isBot, pdpSponsoredMoreSimilarItemsGridTest } = useServerVarsContext();
    const isInPdpSponsoredGridTest = !!pdpSponsoredMoreSimilarItemsGridTest;
    const isPdpSponsoredMoreSimilarItemsGridVariant =
        pdpSponsoredMoreSimilarItemsGridTest === 'variant';

    useEffect(() => {
        const userId = getBuyerId(document.cookie) || '';
        const userType = getUserType(document.cookie);
        const guestId = getGuestId(document.cookie) || '';
        const userCountryCode = getUserSessionCountryCode();

        // Grid AB (both arms): pageSize = Topsort/eligibleItemIds pool; maxItems = inflate cap
        // (SponsoredResult partial-inflate pattern). Control: carousel only; variant: carousel+grid.
        // When the AB is off, omit these so the query defaults apply.
        const sponsoredFetchVars = isInPdpSponsoredGridTest
            ? {
                  numOfSponsoredItems: PDP_INFINITE_SCROLL_SPONSORED_PAGE_SIZE,
                  maxSponsoredItems: isPdpSponsoredMoreSimilarItemsGridVariant
                      ? PDP_INFINITE_SCROLL_SL_INITIAL_INFLATE
                      : PDP_INFINITE_SCROLL_CAROUSEL_SL_COUNT,
                  // Snap inflate to carousel sizes (6–9) and full 5-col grid breakpoints when maxItems is unreachable.
                  minSponsoredItemsSteps: [5, 6, 7, 8, 9, 10, 15, 20] as number[],
              }
            : {};

        relayVariables.current = {
            ...serverRelayVariables,
            userId,
            guestId,
            userCountryCode,
            afterDisplay: true,
            afterDisplayAndBtNone: !isBot,
            fetchUser: !!userId,
            isTrade: userType === CUSTOMER_TYPE_TRADE,
            isPdpSponsoredMoreSimilarItemsGridVariant,
            ...sponsoredFetchVars,
        };
    }, [
        isBot,
        serverRelayVariables,
        isInPdpSponsoredGridTest,
        isPdpSponsoredMoreSimilarItemsGridVariant,
    ]);

    const loadQuery = useCallback(() => {
        if (hasLoadedRef.current) {
            return;
        }
        hasLoadedRef.current = true;
        loadSecondaryQuery(relayVariables.current);
    }, [loadSecondaryQuery]);

    useEffect(() => {
        if (hasLoadedRef.current || secondaryQueryRef) {
            return undefined;
        }
        window.addEventListener('scroll', loadQuery);
        return () => {
            window.removeEventListener('scroll', loadQuery);
        };
    }, [loadQuery, secondaryQueryRef]);

    return (
        <>
            <VisibilityTracker
                elementRef={layoutVisibilityRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        loadQuery();
                        window.removeEventListener('scroll', loadQuery);
                    }
                }}
            />
            <div ref={layoutVisibilityRef} />
            {/* secondaryQueryRef exists if initial queryRef was provided on server (for bots) or load query was called on client */}
            {secondaryQueryRef ? (
                <Suspense fallback={<LoadingCarousel />}>
                    <PdpRespMarketplaceLayoutSecondaryLazy
                        queryRef={secondaryQueryRef}
                        relayVariables={relayVariables.current}
                    />
                </Suspense>
            ) : (
                <LoadingCarousel />
            )}
        </>
    );
};
